import {isLowBatteryDevice} from './usePowerSourceIcon.js';

export const NON_REPORTING_HOURS = 30;

// API timestamps are UTC but lack timezone suffix - ensure parsed as UTC
const parseUtcTimestamp = (ts) => {
    if (!ts) return NaN;
    let s = String(ts);
    if (!s.endsWith('Z') && !s.includes('+') && !s.includes('T')) {
        s = s.replace(' ', 'T') + 'Z';
    }
    return new Date(s).getTime();
};

// A member who deliberately PAUSED sharing. Distinct from every telemetry-derived
// state, and it has to be checked first: the API collapses this into
// fleet_status 'offline' (DeviceLiveViewResource: `$sharingOff ? 'offline' : ...`)
// regardless of how recently the device reported, so trusting fleet_status alone
// reports a phone that checked in 40 minutes ago as having lost signal.
//
// Those are opposite things to a family. "Offline" says we cannot reach them and
// something may be wrong; "Sharing paused" says they chose this and nothing is.
// sharing_off is sent precisely so a client can tell them apart.
export const isSharingOff = (device) => device?.sharing_off === true;

export const isNonReporting = (device) => {
    // Paused is a decision, not a failure - it must never be counted as offline.
    if (isSharingOff(device)) return false;
    if (device.fleet_status) return device.fleet_status === 'offline';
    if (!device.last_message_received) return true;
    const hoursDiff = (Date.now() - parseUtcTimestamp(device.last_message_received)) / (1000 * 60 * 60);
    return hoursDiff > NON_REPORTING_HOURS;
};

export const isRecentlyReported = (device) => {
    if (!device.last_message_received) return false;
    const minsDiff = (Date.now() - parseUtcTimestamp(device.last_message_received)) / (1000 * 60);
    return minsDiff <= 3;
};

export const isIdle = (device) => {
    if (device.fleet_status) return device.fleet_status === 'idling';
    return device.motion_state === 1 && (device.gps_speed || 0) <= 2 && isRecentlyReported(device);
};
export const isMoving = (device) => {
    if (device.fleet_status) return device.fleet_status === 'moving';
    return device.motion_state === 1 && !isIdle(device) && !isNonReporting(device);
};
export const isStationary = (device) => {
    // A paused member is not "parked" either - their last point is frozen, so
    // claiming they are stationary asserts something we cannot know.
    if (isSharingOff(device)) return false;
    if (device.fleet_status) return device.fleet_status === 'parked';
    return !isMoving(device) && !isIdle(device) && !isNonReporting(device);
};
// One predicate for the chip count/filter AND the row/popup icon: percent
// devices (phones, ranged vehicles) at <= 20%, hardware bars < 2. The chip used
// its own bars-only rule, so it counted a phone at 30% (1 bar) that wore no
// red icon, and filtering by the chip hid rows that showed the icon.
export const isLowBattery = (device) => isLowBatteryDevice(device);

// "Moving" filter includes both moving and idling (all active devices)
export const isMovingOrIdle = (device) => isMoving(device) || isIdle(device);

export const fleetFilterFns = {
    total: () => true,
    moving: isMovingOrIdle,
    idle: isIdle,
    stationary: isStationary,
    non_reporting: isNonReporting,
    sharing_off: isSharingOff,
    low_battery: isLowBattery,
};

export const filterDevicesByFleetStatus = (devices, filterKey) => {
    if (!filterKey || filterKey === 'total') return devices || [];
    const fn = fleetFilterFns[filterKey];
    return fn ? (devices || []).filter(fn) : (devices || []);
};
